import type { AuditModule } from '@micarahr/shared-types';

/**
 * Human wording for the audit log.
 *
 * The server stores actions as dotted codes (`leave.request.approve`) and
 * modules as identifiers (`employee_relations`). Codes are the right thing to
 * store — they never change when the wording does — and the wrong thing to
 * show. An auditor reading "leave.type_created" has to translate it; one
 * reading "Created a leave type" does not.
 */

export const AUDIT_MODULE_LABELS: Record<AuditModule, string> = {
  employees: 'Employees',
  organization: 'Organisation',
  attendance: 'Attendance',
  schedules: 'Schedules',
  timesheets: 'Timesheets',
  leave: 'Leave',
  overtime: 'Overtime',
  payroll: 'Payroll',
  compensation: 'Compensation',
  benefits: 'Benefits',
  loans: 'Loans',
  recruitment: 'Recruitment',
  onboarding: 'Onboarding',
  probation: 'Probation',
  performance: 'Performance',
  learning: 'Learning',
  documents: 'Documents',
  policies: 'Policies',
  announcements: 'Announcements',
  assets: 'Assets',
  employee_relations: 'Employee relations',
  offboarding: 'Offboarding',
  users: 'Users',
  roles: 'Roles',
  settings: 'Settings',
  auth: 'Sign-in & security',
  simulation: 'Simulation',
  approvals: 'Approvals',
  devices: 'Devices',
  exports: 'Exports',
  imports: 'Imports',
  media: 'Files',
  platform: 'Platform',
  registries: 'Registries',
};

export function auditModuleLabel(module: string): string {
  return AUDIT_MODULE_LABELS[module as AuditModule] ?? sentenceCase(module);
}

/*
 * The phrases below are the server's (apps/api/src/common/audit/labels.ts),
 * which sends each entry's `actionLabel` and `recordText` and searches them.
 * This copy words an entry the server did not (the offline prototype); the
 * test holds the two copies to one wording.
 */

/**
 * Every action the API is known to write, as a phrase.
 *
 * One form throughout: a past-tense verb first, as a sentence in the log
 * reads — "Updated a department", "Cancelled a compensation change" — and
 * the verb its entries' sentences use, in general form: "Hired Ana Cruz
 * Reyes (CND-2026-0012) as employee MTC-2026-0044" is "Hired a candidate",
 * never "Converted a candidate into an employee".
 *
 * Not exhaustive by necessity: an action added later without an entry here
 * falls through to `humanizeAction`, which still reads as words rather than
 * a code.
 */
export const ACTION_LABELS: Record<string, string> = {
  // Sign-in and security
  'auth.login_succeeded': 'Signed in',
  'auth.login': 'Signed in',
  'auth.login_failed': 'Failed to sign in',
  'auth.magic_link_login_succeeded': 'Signed in with a magic link',
  'auth.magic_link_login_failed': 'Failed to sign in with a magic link',
  'auth.magic_link_requested': 'Requested a sign-in link',
  'auth.magic_link_refused': 'Refused a sign-in link',
  'auth.google_login_succeeded': 'Signed in with Google',
  'auth.google_login_failed': 'Failed to sign in with Google',
  'auth.account_locked': 'Locked an account after failed sign-ins',
  'auth.logout': 'Signed out',
  'auth.logout_all': 'Signed out of every device',
  'auth.session_idle_timeout': 'Signed out after inactivity',
  'auth.mfa_enabled': 'Turned on two-step verification',
  'auth.mfa_disabled': 'Turned off two-step verification',
  'auth.mfa_challenged': 'Completed a two-step verification challenge',
  'auth.mfa_recovery_code_used': 'Used a two-step recovery code',
  'auth.password_changed': 'Changed their password',
  'auth.password_reset_requested': 'Requested a password reset',
  'auth.password_reset_completed': 'Reset their password',
  'auth.refresh_token_reuse_detected': 'Revoked sessions after a reused sign-in token',

  // Users, roles, settings
  'user.created': 'Created a user account',
  'user.roles_changed': "Changed a user's roles",
  'user.roles_change_refused': 'Refused a role change',
  'user.status_change_refused': 'Refused a user status change',
  'user.reactivated': 'Reactivated a user account',
  'user.deactivated': 'Deactivated a user account',
  'user.suspended': 'Suspended a user account',
  'user.temporary_password_issued': 'Issued a temporary password',
  'user.invitation_sent': 'Sent a sign-in invitation',
  'role.permissions_changed': "Changed a role's permissions",
  'role.permissions_change_refused': 'Refused a permission change',
  'role.status_change_refused': 'Refused a role status change',
  'role.activated': 'Activated a role',
  'role.deactivated': 'Deactivated a role',
  'settings.updated': 'Updated company settings',
  'approval_workflow.configured': 'Configured an approval workflow',

  // Organization
  'organization.created': 'Created an organisation record',
  'organization.updated': 'Updated an organisation record',
  'organization.deactivated': 'Deactivated an organisation record',
  'organization.reactivated': 'Reactivated an organisation record',
  // Refused to remove a department (the only record it is written for; see
  // REGISTRY_REFUSALS), in general form.
  'organization.deactivate_refused': 'Refused a deactivation',
  // The profile is a section of Company Settings; its entries say so.
  'organization.profile_updated': 'Updated company settings',
  // "Changed the geofence radius of …", "… the geofence centre of …".
  'work_location.geofence_changed': "Changed a work location's geofence",

  // Employees
  'employee.created': 'Created an employee record',
  'employee.updated': 'Updated an employee record',
  'employee.employment_status_changed': 'Changed employment status',
  'employee.government_references_updated': 'Updated government ID numbers',
  'employee.self_address_updated': 'Updated their own address',
  'employee.self_contact_updated': 'Updated their own contact details',
  'employee.self_emergency_contact_added': 'Added an emergency contact',
  'employee.self_emergency_contact_removed': 'Removed an emergency contact',
  'employee.self_emergency_contact_updated': 'Updated an emergency contact',
  'document.verified': 'Verified an employee document',

  // Attendance and devices
  'attendance.time_in.recorded': 'Recorded time in',
  'attendance.time_out.recorded': 'Recorded time out',
  'attendance.break_start.recorded': 'Started a break',
  'attendance.break_end.recorded': 'Ended a break',
  'attendance.time_in.refused': 'Refused a time in',
  'attendance.time_out.refused': 'Refused a time out',
  'attendance.break_start.refused': 'Refused a break start',
  'attendance.break_end.refused': 'Refused a break end',
  // "Added a note to Ana Cruz Reyes's attendance: …".
  'attendance.note_added': "Added a note to an employee's attendance",
  'attendance.selfie_viewed': 'Viewed an attendance selfie',
  'attendance.marked_absent': 'Marked an employee absent',
  'attendance.evidence_purged': 'Purged expired attendance evidence',
  'attendance.provider_claim_ignored': 'Ignored a location-provider claim',
  // "Approved attendance for Fernando Aquino Ramos on 29 Sep 2026".
  'attendance.review.approve': 'Approved attendance',
  'attendance.review.approve_with_note': 'Approved attendance with a note',
  'attendance.review.reject': 'Rejected attendance',
  'attendance.review.request_explanation': 'Asked an employee to explain their attendance',
  'attendance.review.return_for_correction': 'Returned attendance for correction',
  'attendance.correction.submitted': 'Submitted an attendance correction',
  'attendance.correction.approve': 'Approved an attendance correction',
  'attendance.correction.reject': 'Rejected an attendance correction',
  'attendance.correction.cancelled': 'Cancelled an attendance correction',
  'device.registered': 'Registered a device',
  'device.registration_pending': 'Registered a device for approval',
  'device.approved': 'Approved a device',
  'device.revoked': 'Revoked a device',
  'device.re_registration_requested': 'Requested device re-registration',
  'device.signing_key_issued': 'Issued a device signing key',

  // Schedules, timesheets
  // The schedules registry covers shift templates and holidays, not
  // assignments; the record type column says which.
  'schedules.created': 'Created a shift template or holiday',
  'schedules.updated': 'Updated a shift template or holiday',
  'schedules.deactivated': 'Deactivated a shift template',
  'schedules.reactivated': 'Reactivated a shift template',
  'schedule.bulk_assigned': 'Assigned schedule days',
  'schedule.published': 'Published schedule days',
  // "Withdrew 5 schedule days for 1 employee on 12 Oct 2026".
  'schedule.unpublished': 'Withdrew schedule days',
  'schedule.removed_after_separation': 'Removed schedule days after a last working day',
  'timesheet.generated': 'Generated timesheets',
  'timesheet.period.locked': 'Locked a timesheet period',
  'timesheet.period.unlocked': 'Unlocked a timesheet period',

  // Leave and overtime
  'leave.request.submitted': 'Submitted a leave request',
  'leave.request.approve': 'Approved a leave request',
  'leave.request.reject': 'Rejected a leave request',
  'leave.request.return': 'Returned a leave request',
  'leave.request.withdrawn': 'Withdrew a leave request',
  'leave.request.cancelled_after_approval': 'Cancelled approved leave',
  'leave.request.cancelled_by_employee': 'Cancelled their leave request',
  'leave.balance.adjusted': 'Adjusted a leave balance',
  'leave.type_created': 'Created a leave type',
  'leave.type_updated': 'Updated a leave type',
  'overtime.request.submitted': 'Submitted an overtime request',
  'overtime.request.approve': 'Approved an overtime request',
  'overtime.request.reject': 'Rejected an overtime request',
  'overtime.request.return': 'Returned an overtime request',
  'overtime.request.cancelled': 'Cancelled an overtime request',
  'overtime.marked_payroll_ready': 'Marked overtime ready for payroll',

  // Payroll, compensation, benefits, loans
  'payroll.calculated': 'Calculated payroll',
  'payroll.payslips_published': 'Published payslips',
  'payroll.configuration.table_versioned': 'Published a new contribution table version',
  // "Opened payroll period October 2026".
  'payroll.period.created': 'Opened a payroll period',
  'payroll.period.draft': 'Returned a payroll period to draft',
  // Entering attendance review is the import: its sentence says "Imported
  // attendance for September 2026".
  'payroll.period.attendance_review': 'Imported attendance into payroll',
  'payroll.period.input_review': 'Moved payroll to input review',
  'payroll.period.calculating': 'Started calculating a payroll period',
  'payroll.period.calculated': 'Calculated a payroll period',
  'payroll.period.for_approval': 'Sent payroll for approval',
  'payroll.period.approved': 'Approved a payroll period',
  'payroll.period.locked': 'Locked a payroll period',
  'payroll.period.payslips_published': 'Published payslips',
  'payroll.period.completed': 'Marked a payroll period completed',
  'payroll.period.reopened': 'Reopened a payroll period',
  'payroll.period.cancelled': 'Cancelled a payroll period',
  'payroll.payslips_withdrawn': 'Withdrew payslips',
  'payroll.employee.excluded': 'Excluded an employee from a payroll period',
  'payroll.employee.included': 'Included an employee in a payroll period',
  'payroll.exception.resolved': 'Resolved a payroll exception',
  'payslip.acknowledged': 'Acknowledged a payslip',
  // "Recorded a merit increase for Camille Tan Mendoza, effective …".
  'compensation.changed': 'Recorded a compensation change',
  'compensation.change_submitted': 'Submitted a compensation change for approval',
  'compensation.change_approved': 'Approved a compensation change',
  'compensation.change_rejected': 'Rejected a compensation change',
  'compensation.change_cancelled': 'Cancelled a compensation change',
  // "Viewed Camille Tan Mendoza's compensation".
  'compensation.viewed': "Viewed an employee's compensation",
  'compensation.recurring_item_added': 'Added a recurring pay item',
  'compensation.recurring_item_ended': 'Ended a recurring pay item',
  'benefits.enrolled': 'Enrolled an employee in a benefit',
  'loan.approved': 'Approved a loan',
  'loan.rejected': 'Rejected a loan',
  'loan.active': 'Activated a loan',
  'loan.closed': 'Closed a loan',
  'loan.completed': 'Settled a loan',
  'loan.cancelled': 'Cancelled a loan',

  // Assets, policies, announcements
  'asset.assigned': 'Assigned an asset',
  'asset.returned': 'Recorded an asset return',
  'asset.status_changed': "Changed an asset's status",
  'policy.published': 'Published a policy',
  'announcement.published': 'Published an announcement',

  // Recruitment and onboarding
  'recruitment.requisition_raised': 'Raised a requisition',
  'recruitment.requisition_approved': 'Approved a requisition',
  // "Filled requisition REQ-2026-0026 for “Frontend Developer”: 1 of 1 hired".
  'recruitment.requisition_filled': 'Filled a requisition',
  'recruitment.candidate_added': 'Added a candidate',
  'recruitment.candidate_stage_moved': 'Moved a candidate to a new stage',
  'recruitment.candidate_rejected': 'Rejected a candidate',
  'recruitment.candidate_withdrawn': 'Recorded a candidate as withdrawn',
  'recruitment.interview_scheduled': 'Scheduled an interview',
  'recruitment.interview_outcome_recorded': 'Recorded an interview outcome',
  'recruitment.offer_extended': 'Extended an offer',
  'recruitment.offer_accepted': 'Recorded an offer as accepted',
  'recruitment.candidate_converted': 'Hired a candidate',
  // "Opened onboarding for Ana Cruz Reyes".
  'onboarding.case_opened': 'Opened onboarding for an employee',
  'onboarding.task_completed': 'Completed an onboarding task',
  'onboarding.completed': 'Completed onboarding',

  // Cases
  'probation.confirm_employment': 'Confirmed employment after probation',
  'probation.extend': 'Extended probation',
  'probation.end_employment': 'Ended employment at probation',
  'probation.return_for_information': 'Returned a probation review for information',
  'performance.finalized': 'Finalised a performance review',
  'performance.cycle_status_changed': "Changed a performance cycle's status",
  'employee_relations.opened': 'Opened an employee relations case',
  'employee_relations.entry_added': 'Added an employee relations case entry',
  'employee_relations.listed': 'Viewed the employee relations case list',
  'offboarding.case_opened': 'Opened an offboarding case',
  'offboarding.clearance_task_completed': 'Completed an offboarding task',
  // "Disabled the login for katrina.santos@… (SEP-2026-0018)"; the entry for
  // a leaver with no login reads by its variant below.
  'offboarding.access_revoked': "Disabled a leaver's login",
  'offboarding.completed': 'Completed an offboarding case',

  // Platform
  'export.requested': 'Requested an export',
  'export.completed': 'Completed an export',
  'import.committed': 'Committed an import',
  'media.infected': 'Blocked an infected upload',
  'media.scan_failed': 'Failed to scan an upload',
};

/**
 * One action code, several events: the phrase for each, keyed
 * `action#variant` (the server's `auditActionVariant`). Ticking "Revoke system access"
 * for a leaver with no login revoked nothing, and its sentence says so; its
 * phrase says so too.
 */
/**
 * The one name for work nobody pressed a button for — the worker's scheduled
 * jobs: a day closed as absent, a leaver's login disabled overnight. The
 * server's `SCHEDULED_ACTOR_NAME`; "System" on one screen beside this on
 * another was two names for one actor.
 */
export const SCHEDULED_ACTOR_NAME = 'MicaraHR (scheduled)';

export const ACTION_VARIANT_LABELS: Record<string, string> = {
  'offboarding.access_revoked#no_login': 'Completed task “Revoke system access”',
  'asset.status_changed#maintenance': 'Sent an asset for maintenance',
  'asset.status_changed#damaged': 'Marked an asset damaged',
  'asset.status_changed#lost': 'Reported an asset lost',
  'asset.status_changed#retired': 'Retired an asset',
  'asset.status_changed#available': 'Returned an asset to service',
  'leave.balance.adjusted#added': 'Added days to a leave balance',
  'leave.balance.adjusted#removed': 'Removed days from a leave balance',
  'device.revoked#self': 'Removed their own device',
  'organization.updated#department_renamed': 'Renamed a department',
  'organization.updated#department_head': "Changed a department's head",
  'organization.updated#department_head_removed': "Removed a department's head",
  'organization.updated#team_lead_removed': "Removed a team's lead",
};

function sentenceCase(value: string): string {
  const words = value
    .replace(/[._-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '—';
}

/**
 * Fallback for a code with no phrase: its words, in order, sentence-cased —
 * "registries.created" becomes "Registries created". Never the dots.
 */
export function humanizeAction(action: string): string {
  return sentenceCase(action);
}

/**
 * What each registry's rows are called. The registries share an action per
 * module — `organization.updated` is a department, a team, a position, a
 * cost centre, a work location or a payroll group — so the phrase for the
 * action alone could only say "an organisation record". With the entry's
 * record type it names which.
 */
export const REGISTRY_LABEL_NOUNS: Record<string, string> = {
  department: 'a department',
  team: 'a team',
  position: 'a position',
  job_level: 'a job level',
  cost_center: 'a cost centre',
  work_location: 'a work location',
  payroll_group: 'a payroll group',
  shift_template: 'a shift template',
  holiday: 'a holiday',
};

/**
 * The verbs a registry change is written with — departments are removed and
 * restored, as their screen and the server's sentence say ("Removed
 * department Research"); the rest are deactivated and reactivated.
 */
const REGISTRY_VERBS: Record<string, string> = {
  created: 'Created',
  updated: 'Updated',
  deactivated: 'Deactivated',
  reactivated: 'Reactivated',
};
const DEPARTMENT_VERBS: Record<string, string> = {
  deactivated: 'Removed',
  reactivated: 'Restored',
};

/** "Refused to remove department Sales: 3 employees still assigned". */
const REGISTRY_REFUSALS: Record<string, string> = {
  department: 'Refused to remove a department',
};

/**
 * The action as a phrase. Given the entry's record type, a registry action
 * names the kind of record — "Removed a department", "Updated a position" —
 * and given its variant, which of the action's events it was.
 */
export function auditActionLabel(
  action: string,
  recordType?: string | null,
  variant?: string | null,
): string {
  if (variant) {
    const labelled = ACTION_VARIANT_LABELS[`${action}#${variant}`];
    if (labelled) return labelled;
  }
  if (action === 'organization.deactivate_refused' && recordType) {
    const refusal = REGISTRY_REFUSALS[recordType];
    if (refusal) return refusal;
  }
  const registry = /^(?:organization|schedules)\.(created|updated|deactivated|reactivated)$/.exec(
    action,
  )?.[1];
  const noun = recordType ? REGISTRY_LABEL_NOUNS[recordType] : undefined;
  if (registry && noun) {
    const verb =
      (recordType === 'department' ? DEPARTMENT_VERBS[registry] : undefined) ??
      REGISTRY_VERBS[registry];
    return `${verb} ${noun}`;
  }
  return ACTION_LABELS[action] ?? humanizeAction(action);
}

/**
 * Record types whose code does not say what a reader edited. The organisation
 * row and the settings row are both changed from Company Settings — the
 * page's own name. A roster change is about schedule days, as its sentence
 * says, not one assignment.
 */
export const RECORD_TYPE_LABELS: Record<string, string> = {
  organization: 'Company settings',
  organization_settings: 'Company settings',
  schedule_assignment: 'Schedule days',
  media: 'File',
  session: 'Sign-in session',
};

/** Entries whose record is named by what was done, not by its storage type. */
export const ACTION_RECORD_TYPES: Record<string, string> = {
  'attendance.selfie_viewed': 'Attendance selfie',
  // Written against the employee; it is about their schedule days.
  'schedule.removed_after_separation': 'Schedule days',
  'employee_relations.listed': 'Employee relations cases',
};

/** The identifier an entry has no record row for, by its action. */
export const ACTION_RECORD_IDENTIFIERS: Record<string, string> = {
  'employee_relations.listed': 'Case list',
  'organization.profile_updated': 'Company profile',
};

/**
 * `job_requisition` → "Job requisition". The codes are American
 * (`organization`); the screen's English is not — "Organisation".
 */
export function auditRecordTypeLabel(recordType: string | null | undefined): string {
  if (!recordType) return '—';
  return (
    RECORD_TYPE_LABELS[recordType] ??
    sentenceCase(recordType).replace(/([Oo])rganization/g, '$1rganisation')
  );
}

/**
 * What the entry's record is, always as "Type · identifier" —
 * "Company settings · Company profile", "Attendance selfie · Ana Cruz Reyes,
 * 29 Sep 2026". An entry the server could not name falls back to its stamped
 * reference, then its person.
 */
export function auditRecordText(row: {
  action: string;
  recordType?: string | null;
  recordLabel?: string | null;
  recordIdentifier?: string | null;
  subjectName?: string | null;
}): string {
  const type =
    ACTION_RECORD_TYPES[row.action] ??
    (row.recordType ? auditRecordTypeLabel(row.recordType) : null);
  const identifier =
    row.recordIdentifier ||
    row.recordLabel ||
    row.subjectName ||
    ACTION_RECORD_IDENTIFIERS[row.action] ||
    null;
  if (!type) return identifier ?? '—';
  return identifier ? `${type} · ${identifier}` : type;
}

const WITHHELD_WORDS: Record<string, string> = {
  reason: 'the reason',
  note: 'the note',
  comment: 'the comment',
  remarks: 'the remarks',
  feedback: 'the feedback',
  values: 'the values before and after',
};

/**
 * What an entry's panel says of detail its reader is not shown, as a list of
 * exactly what the server withheld — "Hidden from you: the reason, the note
 * and the values before and after." — or null when it withheld nothing. "This
 * entry's reason and before and after values are shown only to…" read as one
 * run-on noun.
 */
export function auditWithheldNotice(withheld: string[]): string | null {
  if (withheld.length === 0) return null;
  const words = [...new Set(withheld.map((item) => WITHHELD_WORDS[item] ?? `the ${item}`))];
  const listed =
    words.length === 1 ? words[0] : `${words.slice(0, -1).join(', ')} and ${words.at(-1)}`;
  return `Hidden from you: ${listed}. Only people allowed to see sensitive audit detail are shown ${words.length === 1 && !/values/.test(words[0]!) ? 'it' : 'them'}.`;
}
